import type { PersonaProfile } from '@/types/personas';
import type { ScenarioUser } from '@/types';

export interface EffectivePersona {
  id: string;
  display_name: string;
  sharePercent: number;

  inputTokensPerTurn: number;
  outputTokensPerTurn: number;

  readSec: number;
  thinkSec: number;

  latencyMode: 'snappy' | 'fast' | 'responsive' | 'comfy' | 'async';
}

export function resolveEffectivePersona(
  user: ScenarioUser,
  profilesById: Record<string, PersonaProfile>,
  defaultLatencyMode: 'snappy' | 'fast' | 'responsive' | 'comfy' | 'async',
  totalUsers: number,
): EffectivePersona {
  const profile =
    user.profileId && user.profileId !== 'custom'
      ? profilesById[user.profileId] ?? undefined
      : undefined;

  // Prefer explicit user percentage; fall back to profile mix_percent only if user pct is absent.
  let sharePercent = user.pct ?? profile?.mix_percent ?? null;
  if (!sharePercent || sharePercent <= 0) {
    sharePercent = 100 / Math.max(1, totalUsers);
  }

  const displayName = user.label ?? profile?.display_name ?? 'Custom';

  const inputTokensPerTurn = profile?.prompt_tokens_per_turn ?? user.inTok ?? 0;
  const outputTokensPerTurn = profile?.response_tokens_per_turn ?? user.outTok ?? 0;

  const readSec = Number(profile?.read_sec_default ?? user.read_sec ?? 0);
  const thinkSec = Number(profile?.think_sec_default ?? user.digest_sec ?? 0);

  const latencyMode = user.latencyMode ?? defaultLatencyMode;

  const resolved: EffectivePersona = {
    id: user.uid ?? user.id ?? user.profileId ?? displayName,
    display_name: displayName,
    sharePercent,
    inputTokensPerTurn,
    outputTokensPerTurn,
    readSec,
    thinkSec,
    latencyMode,
  };

  if (process.env.NODE_ENV !== 'production') {
    console.log('[PersonaResolver] resolved', {
      userUid: user.uid,
      userId: user.id,
      profileId: user.profileId,
      displayName,
      sharePercent,
      inputTokensPerTurn,
      outputTokensPerTurn,
      readSec,
      thinkSec,
      latencyMode,
      hasProfile: Boolean(profile),
    });
  }

  return resolved;
}
